import React from "react";
import DialogBoxWithConfirmation from "../../util/DialogBoxWithConfirmation";
import { useTranslation } from "react-i18next";
import { URL_VIDEO } from "../../map/GPX/Resources";
import { roadmakerApi } from "../../../api/roadmakerApi";
import { normalizeFileName } from "../../util/Util";
import {
  discardPoiMediaUploads,
  pendingPoiMediaPreview,
  republishMarkerMedia,
} from "../../../api/poiMediaUploads";
import {
  findServerMediaByName,
  resolveMediaSource,
} from "./mediaSource";

const MarkerVideo = ({
  markerVideo,
  marker,
  setMarkerVideo,
  markerVideoName,
  setMarkerVideoName,
  setMarkers,
}) => {
  const { t } = useTranslation();

  // Source rétrocompatible centralisée dans ./mediaSource.
  const mediaNameAt = (index) =>
    markerVideoName?.[index] ?? marker.videoName?.[index];

  const serverMediaAt = (index) =>
    findServerMediaByName(marker, "video", mediaNameAt(index));

  // Une pièce importée mais pas encore envoyée est lue depuis le fichier
  // local : sans cela, l'aperçu n'apparaissait qu'après la sauvegarde. Même
  // correspondance de nom que la suppression ; un média serveur du même nom
  // garde sa propre source.
  const pendingPreviewAt = (index) => {
    const name = mediaNameAt(index);
    if (!name || serverMediaAt(index)) return undefined;
    return pendingPoiMediaPreview(
      marker.id,
      "video",
      (item) =>
        item.file.name === name || normalizeFileName(item.file.name) === name
    );
  };

  // Source locale sans attribut type : le type réel d'un .mov
  // (video/quicktime) est refusé d'office par Chrome et Firefox, qui lisent
  // pourtant le fichier ; le navigateur reconnaît le contenu lui-même.
  const videoSource = (video, index) => {
    const localUrl = pendingPreviewAt(index);
    if (localUrl) return { url: localUrl };
    return {
      url: resolveMediaSource(marker, "video", { value: video, index }),
      type: "video/mp4",
    };
  };

  // La clé suit la source : un <video> ne relit pas un <source> modifié.
  const renderVideo = ({ url, type }) => (
    <video key={url} controls className="videoInput">
      <source src={url} type={type} />
    </video>
  );

  /**
   * Permet de lancer la suppression d'une vidéo du marqueur
   * @param {integer} index - Index de la vidéo à supprimer du marqueur
   */
  const handleDeleteVideo = (index) => async (e) => {
    console.log("jest: Fonction de suppression est appelée");
    e.stopPropagation();
    const confirmDelete = await DialogBoxWithConfirmation({
      title: t("deleteVideoText"),
      text: t("deleteVideoConfirmation"),
      icon: "warning",
      cancelButtonText: t("no"),
      confirmButtonText: t("yes"),
    });

    // Confirmer la suppression
    if (confirmDelete) {
      // Suppression serveur si le média est persisté (ADR 0005) ; un
      // média local non encore sauvé est seulement retiré de l'état.
      const serverMedia = serverMediaAt(index);
        if (serverMedia) {
          if (!marker.serverId || !marker.Project_id) return;
        try {
          await roadmakerApi.poiMedia.remove(
            marker.Project_id,
            marker.serverId,
            serverMedia
          );
        } catch (error) {
          console.error("Échec de la suppression du média serveur :", error);
          return;
        }
        marker.media = (marker.media || []).filter(
          (media) => media.fileName !== serverMedia.fileName
        );
        // La consultation doit refléter la suppression sans rechargement
        // (retest 2026-09-04 : doublon persistant pendant la navigation).
        republishMarkerMedia(marker, setMarkers);
      }
      const name = mediaNameAt(index);
        const discarded = discardPoiMediaUploads(
        marker.id,
        (item) =>
          item.mediaType === "video" &&
          (item.file.name === name || normalizeFileName(item.file.name) === name)
      );
        if (!serverMedia && marker.serverId && discarded === 0) return;
      setMarkerVideo(markerVideo.filter((_, i) => i !== index));
      setMarkerVideoName(markerVideoName.filter((_, i) => i !== index));
    }
  };

  return (
    <div>
      {markerVideo &&
        markerVideo.map((video, index) => (
          <div key={index}>
            <div>{markerVideoName && markerVideoName[index]}</div>
            {renderVideo(videoSource(markerVideo[index], index))}
            <br />
            {(markerVideo.length > 1 ||
              (markerVideo.length === 1 && index === 0)) && (
              <button
                type="button"
                className="buttonDeleteFile mt-3 mb-3 me-2"
                onClick={handleDeleteVideo(index)}
                aria-label="delete"
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        ))}
      {!markerVideo &&
        marker.video &&
        marker.video.map((video, index) => (
          <div key={index}>
            <div>{marker.videoName && marker.videoName[index]}</div>
            {renderVideo(videoSource(marker.video?.[index], index))}
            <br />
          </div>
        ))}
    </div>
  );
};

export default MarkerVideo;
